import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronLeft, ChevronRight, MessageCircle, ShoppingCart, Star, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { getProduct } from '../services/api';
import usePageSEO from '../hooks/usePageSEO';
import { getWhatsAppProductUrl, getWhatsAppShareUrl } from '../lib/whatsapp';
import { isPriceOnRequest } from '../lib/pricing';

const getProductImageList = (product) => {
  const seen = new Set();
  return [product?.image_url, ...(product?.images || [])]
    .map((value) => (value || '').trim())
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const ProductDetail = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const lang = i18n.language?.substring(0, 2) || 'en';
  const productName = product?.name?.[lang] || product?.name?.en || product?.name?.ar || '';
  const productDescription = product?.description?.[lang] || product?.description?.en || product?.description?.ar || '';
  const productCategory = product?.category?.[lang] || product?.category?.en || product?.category?.ar || '';
  const seoTitle = loading ? '' : (productName || t('pages.productNotFound'));
  const hasRequestPrice = product ? isPriceOnRequest(product.price_min) : true;
  const canOrderProduct = product && !hasRequestPrice && product.stock_quantity > 0;
  const canRequestQuote = product && hasRequestPrice;
  const productImages = useMemo(() => getProductImageList(product), [product]);
  const selectedImage = productImages[selectedImageIndex] || productImages[0] || product?.image_url || '';
  const productShareUrl = useMemo(() => {
    if (!product) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.itronixbh.com';
    const url = new URL(`/shop/${product.id}`, `${origin}/`);
    if (productImages.length > 1) url.searchParams.set('photo', String(selectedImageIndex + 1));
    return url.href;
  }, [product, productImages.length, selectedImageIndex]);

  const productStructuredData = useMemo(() => {
    if (!product || !productName) return null;

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.itronixbh.com';
    const productUrl = `${origin}/shop/${product.id}`;
    const imageUrls = productImages.map((image) => new URL(image, `${origin}/`).href);

    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: productName,
      description: productDescription,
      image: imageUrls.length ? imageUrls : undefined,
      sku: product.id,
      category: productCategory || undefined,
      brand: {
        '@type': 'Brand',
        name: product.category?.en || productCategory || 'Itronix',
      },
      offers: !isPriceOnRequest(product.price_min) ? {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: product.currency || 'BHD',
        price: Number(product.price_min || 0).toFixed(3),
        availability: product.stock_quantity > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
      } : undefined,
    };
  }, [product, productName, productDescription, productCategory, productImages]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getProduct(productId);
        setProduct(data);
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productId]);

  useEffect(() => {
    const requestedPhoto = Number.parseInt(searchParams.get('photo'), 10);
    const requestedIndex = Number.isInteger(requestedPhoto) ? requestedPhoto - 1 : 0;
    setSelectedImageIndex(requestedIndex >= 0 && requestedIndex < productImages.length ? requestedIndex : 0);
  }, [productId, productImages.length, searchParams]);

  usePageSEO({
    title: seoTitle,
    description: productDescription,
    keywords: product ? `${product.name?.en || productName}, ${product.category?.en || ''}, car electronics bahrain` : '',
    image: productImages[0] || product?.image_url,
    type: 'product',
    structuredData: productStructuredData,
    defer: loading
  });

  const handleAddToCart = async () => {
    if (!product) return;
    await addToCart(product.id);
    setAdded(true);
    toast({ title: "Added to Cart", description: product.name[lang] });
    setTimeout(() => setAdded(false), 2000);
  };

  const showPreviousImage = () => {
    if (productImages.length < 2) return;
    setSelectedImageIndex((current) => (current === 0 ? productImages.length - 1 : current - 1));
  };

  const showNextImage = () => {
    if (productImages.length < 2) return;
    setSelectedImageIndex((current) => (current + 1) % productImages.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 pb-16 text-center py-20">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('pages.productNotFound')}</h1>
        <Button asChild className="bg-red-600 hover:bg-red-700"><Link to="/shop">{t('pages.backToShop')}</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="product-detail-page">
      <div className="container mx-auto px-4">
        <Link to="/shop" className="inline-flex items-center text-gray-500 hover:text-red-600 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />{t('pages.backToShop')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="relative">
              <img
                src={selectedImage}
                alt={productName}
                className="aspect-[4/3] w-full rounded-2xl object-contain shadow-lg bg-gray-50"
                data-testid="product-detail-image"
              />
              <Badge className="absolute top-4 left-4 bg-red-600 text-white">{productCategory}</Badge>
              {productImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={showPreviousImage}
                    className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow hover:bg-white"
                    aria-label="Previous product photo"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={showNextImage}
                    className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow hover:bg-white"
                    aria-label="Next product photo"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <Badge className="absolute bottom-4 right-4 bg-gray-900/85 text-white">
                    {selectedImageIndex + 1}/{productImages.length}
                  </Badge>
                </>
              )}
            </div>
            {productImages.length > 1 && (
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                {productImages.map((image, index) => (
                  <button
                    type="button"
                    key={`${image}-${index}`}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square rounded-lg border-2 bg-gray-50 p-1 transition ${index === selectedImageIndex ? 'border-red-600' : 'border-gray-200 hover:border-gray-400'}`}
                    aria-label={`View product photo ${index + 1}`}
                  >
                    <img src={image} alt={`${productName} ${index + 1}`} className="h-full w-full rounded object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-3 flex items-start justify-between gap-4">
              <h1 className="flex-1 text-3xl lg:text-4xl font-bold text-gray-900" data-testid="product-detail-name">
                {product.name[lang]}
              </h1>
              <div className="flex shrink-0 flex-col items-center gap-1">
                <span className="text-xs font-medium text-gray-500">{t('pages.share')}</span>
                <Button size="icon" variant="outline" className="border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800" asChild data-testid="product-share-whatsapp">
                  <a href={getWhatsAppShareUrl({ productName, productUrl: productShareUrl, language: lang })} target="_blank" rel="noopener noreferrer" aria-label={t('pages.shareOnWhatsApp')}>
                    <MessageCircle className="h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />)}
              </div>
              <span className="text-gray-500 text-sm">({t('pages.customerFavorite')})</span>
            </div>

            <div className="mb-6">
              <span className="text-3xl sm:text-4xl font-bold text-red-600" data-testid="product-detail-price">
                {!hasRequestPrice ? `${product.price_min.toFixed(3)} BHD` : t('pages.priceOnRequest')}
              </span>
              {!hasRequestPrice && product.price_max && product.price_max > product.price_min && (
                <span className="text-lg text-gray-500 ml-2">- {product.price_max.toFixed(3)} BHD</span>
              )}
            </div>

            <p className="text-gray-600 text-lg mb-8 leading-relaxed" data-testid="product-detail-desc">
              {product.description[lang]}
            </p>

            {canRequestQuote && (
              <p className="mb-6 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
                {t('pages.quoteHint')}
              </p>
            )}

            {/* Specs */}
            {product.specs && product.specs.length > 0 && (
              <div className="mb-8">
                <h3 className="font-bold text-gray-900 mb-3">{t('pages.specifications')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.specs.map((spec, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3" data-testid={`product-spec-${idx}`}>
                      <Check className="h-4 w-4 text-green-500 shrink-0" />
                      <span className="text-sm text-gray-700">{spec[lang]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stock */}
            <div className="mb-6">
              {canOrderProduct ? (
                <Badge className="bg-green-100 text-green-700">
                  {`${t('pages.inStock')} (${product.stock_quantity} ${t('pages.available')})`}
                </Badge>
              ) : hasRequestPrice ? (
                <Badge className="bg-green-100 text-green-700">{t('pages.availableByRequest')}</Badge>
              ) : (
                <Badge className="bg-yellow-100 text-yellow-700">{t('pages.preorderAvailable')}</Badge>
              )}
            </div>

            <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {['fitmentCheck', 'professionalInstall', 'warrantySupport'].map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  <Check className="h-4 w-4 shrink-0 text-green-500" />
                  <span>{t(`pages.trust.${item}`)}</span>
                </div>
              ))}
            </div>

            {/* Add to Cart */}
            {canOrderProduct ? (
              <Button
                size="lg"
                className={`w-full text-lg py-6 ${added ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                onClick={handleAddToCart}
                data-testid="product-add-to-cart"
              >
                {added ? (
                  <><Check className="mr-2 h-5 w-5" />{t('pages.addedToCart')}</>
                ) : (
                  <><ShoppingCart className="mr-2 h-5 w-5" />{t('pages.addToCart')}</>
                )}
              </Button>
            ) : (
              <Button size="lg" className="w-full text-lg py-6 bg-green-600 hover:bg-green-700" asChild data-testid="product-contact-for-price">
                <a href={getWhatsAppProductUrl(productName, product.id, lang, productShareUrl)} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  {hasRequestPrice ? t('pages.contactForPrice') : t('pages.preorderCta')}
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
